<template>
  <div class="container public">
    <page-header />
    <div class="content-container">
      <div class="anomalies-section">
        <h2 class="section-title">{{ $t('anomaliesPage.title') }}</h2>
        <div class="btn-toolbar">
          <button type="button" class="btn btn-primary mr-3" @click="loadAnomalies">
            <font-awesome-icon icon="rotate" /> &nbsp; {{ $t('anomaliesPage.reload') }}
          </button>
          <div class="form-check form-check-inline include-dismissed">
            <input class="form-check-input" type="checkbox" id="includeDismissed" v-model="includeDismissed" @change="loadAnomalies" />
            <label class="form-check-label" for="includeDismissed">{{ $t('anomaliesPage.includeDismissed') }}</label>
          </div>
        </div>
        <error-row @clearError="clearError" :errorMessage="errorMessage" />
        <div v-show="isLoading" class="is-loading-section">
          <button class="btn btn-primary"><span class="spinner-border spinner-border-sm"></span></button><span> {{ $t('anomaliesPage.isLoading') }} </span>
        </div>
        <div v-show="!isLoading && anomalies.length === 0" id="no-data-section">
          {{ $t('anomaliesPage.tableHasNoEntries') }}
        </div>
        <div v-show="!isLoading && anomalies.length > 0" id="table-section">
          <table class="table table-hover">
            <thead>
              <tr>
                <th class="sort-header" @click="sort('detectedAt')">{{ $t('anomaliesPage.columnDetectedAt') }}</th>
                <th class="sort-header" @click="sort('startofday')">{{ $t('anomaliesPage.columnDay') }}</th>
                <th class="sort-header" @click="sort('facilityName')">{{ $t('anomaliesPage.columnFacility') }}</th>
                <th class="sort-header" @click="sort('sensorName')">{{ $t('anomaliesPage.columnSensor') }}</th>
                <th class="sort-header" @click="sort('kind')">{{ $t('anomaliesPage.columnKind') }}</th>
                <th class="text-end">{{ $t('anomaliesPage.columnObserved') }}</th>
                <th class="text-end">{{ $t('anomaliesPage.columnBaseline') }}</th>
                <th class="text-end">{{ $t('anomaliesPage.columnZ') }}</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="a in sortedAnomalies"
                :key="a.id"
                :class="{ 'table-secondary text-muted': a.dismissed }"
              >
                <td>{{ formatDateTime(a.detectedAt) }}</td>
                <td>{{ a.startofday }}</td>
                <td>{{ a.facilityName || '—' }}</td>
                <td>{{ a.sensorName || ('#' + a.cdcsensorid) }}</td>
                <td>{{ kindLabel(a.kind) }}</td>
                <td class="text-end">{{ formatNumber(a.observedDelta) }}</td>
                <td class="text-end">{{ formatNumber(a.baselineMean) }}</td>
                <td class="text-end">{{ formatNumber(a.zScore, 2) }}</td>
                <td>
                  <button
                    v-if="!a.dismissed"
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    @click="dismiss(a)"
                    :title="$t('anomaliesPage.dismiss')"
                  >
                    <font-awesome-icon icon="check" />
                  </button>
                  <span v-else class="badge bg-secondary">{{ $t('anomaliesPage.dismissed') }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <page-footer />
  </div>
</template>

<script>
import moment from 'moment'
import objectSorter from '@/utils/object-sorter'
import PageHeader from '@/components/PageHeader.vue'
import ErrorRow from '@/components/ErrorRow.vue'
import PageFooter from '@/components/PageFooter.vue'
import PageViewMixin from '@/mixins/PageViewMixin.js'
import anomaliesService from '@/services/anomalies'

import { mapGetters } from 'vuex'

export default {
  name: 'AnomaliesPage',
  mixins: [PageViewMixin],
  components: {
    'page-header': PageHeader,
    'page-footer': PageFooter,
    'error-row': ErrorRow
  },
  data () {
    return {
      anomalies: [],
      isLoading: false,
      errorMessage: '',
      includeDismissed: false,
      sortKey: 'detectedAt',
      sortDir: 'desc',
      initialized: false
    }
  },
  computed: {
    ...mapGetters(['client']),
    isAdmin () {
      return this.client && this.client.role === 4
    },
    sortedAnomalies () {
      return objectSorter.sortedArray(this.anomalies, this.sortDir, this.sortKey)
    }
  },
  mounted () {
    this.evaluateAccess()
  },
  watch: {
    // On a direct page load the client data arrives asynchronously
    // (PageHeader dispatches getClientData) — evaluate again once it's there.
    'client.authenticated' () {
      this.evaluateAccess()
    }
  },
  methods: {
    evaluateAccess () {
      if (!this.client || !this.client.authenticated) {
        // still loading; the 401 interceptor routes logged-out users to /login
        return
      }
      if (!this.isAdmin) {
        this.$router.replace({ name: 'home' })
        return
      }
      if (!this.initialized) {
        this.initialized = true
        this.loadAnomalies()
      }
    },
    loadAnomalies () {
      this.isLoading = true
      this.errorMessage = ''
      anomaliesService.listAnomalies({ limit: 500, includeDismissed: this.includeDismissed })
        .then((data) => {
          this.anomalies = (data && data.anomalies) ? data.anomalies : []
        })
        .catch((error) => {
          this.errorMessage = this.localizedBackendMessage(error.message)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    dismiss (anomaly) {
      anomaliesService.dismissAnomaly(anomaly.id)
        .then(() => {
          anomaly.dismissed = true
          if (!this.includeDismissed) {
            this.anomalies = this.anomalies.filter(a => a.id !== anomaly.id)
          }
        })
        .catch((error) => {
          this.errorMessage = this.localizedBackendMessage(error.message)
        })
    },
    sort (key) {
      if (this.sortKey === key) {
        this.sortDir = (this.sortDir === 'asc') ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = 'asc'
      }
    },
    kindLabel (kind) {
      switch (kind) {
        case 'BACKWARD': return this.$t('anomaliesPage.kindBackward')
        case 'STUCK': return this.$t('anomaliesPage.kindStuck')
        case 'OUTLIER': return this.$t('anomaliesPage.kindOutlier')
        default: return kind || '?'
      }
    },
    formatDateTime (value) {
      return value ? moment(value).format('YYYY-MM-DD HH:mm') : ''
    },
    formatNumber (value, digits = 1) {
      if (value == null || value === '') return ''
      const n = Number(value)
      if (Number.isNaN(n)) return value
      return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits })
    },
    clearError () {
      this.errorMessage = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.anomalies-section {
  .sort-header { cursor: pointer; }
  .include-dismissed { margin-left: 1rem; }
}
</style>
